import React, { useEffect, useMemo, useState } from 'react';
import { Search, SlidersHorizontal, Map, List, Users } from 'lucide-react';

import { AppProvider, useApp } from './context/PetParksContext';
import { GoogleMapsScriptProvider } from './context/GoogleMapsScriptProvider';
import type { Pet, Park } from './types';
import { useAnalytics } from './hooks/useAnalytics';
import { useFeatureFlags } from './hooks/useFeatureFlags';

import { PetSelector } from './components/PetSelector';
import { StatsCard } from './components/StatsCard';
import { ListView } from './components/ListView';
import { MapView } from './components/MapView';
import { ParkDetail } from './components/ParkDetailEnhanced';
import { FilterPanel } from './components/FilterPanel';
import { OfflineIndicator } from './components/OfflineIndicator';
import { WalkingBuddyPanel } from './components/WalkingBuddyPanel';
import { DashboardHeaderControls } from '../../components/DashboardHeaderControls';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from './components/ui/sheet';
import { config } from '@/config/config';

type PetsoPet = {
  id: string;
  name: string;
  breed?: string;
  species?: string;
  imageUrl?: string;
};

export type PetParksProps = {
  pets: PetsoPet[];
  selectedPetId?: string;
  onSelectPetId?: (petId: string) => void;
};

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    // Used only to decide mobile-sheet vs desktop-inline overlays.
    // The core layout uses CSS breakpoints (so the map can't "disappear").
    // Align with Tailwind `md` so desktop overlays match the two-column layout.
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

const PetParksContent: React.FC = () => {
  const {
    view,
    setView,
    filteredParks,
    parks,
    selectedPark,
    setSelectedPark,
    searchQuery,
    setSearchQuery,
    activeFilterCount,
    t,
  } = useApp();

  const [showFilters, setShowFilters] = useState(false);
  const [showWalkingBuddy, setShowWalkingBuddy] = useState(false);
  const isDesktop = useIsDesktop();
  const analytics = useAnalytics();
  const { isEnabled } = useFeatureFlags();

  // Track page view on mount
  useEffect(() => {
    analytics.pageView('pet-parks');
  }, [analytics]);

  // Track search
  useEffect(() => {
    if (searchQuery) {
      analytics.searchPerformed(searchQuery, filteredParks.length, {});
    }
  }, [analytics, searchQuery, filteredParks.length]);

  const handleParkSelect = (park: Park) => {
    setSelectedPark(park);
    analytics.parkViewed(park.id, park.name, view === 'map' ? 'map' : 'list');
  };

  // Calculate stats
  const avgRating =
    parks.length > 0
      ? parks.reduce((sum, park) => sum + park.rating, 0) / parks.length
      : 0;

  return (
    <div className="bg-background h-full flex flex-col overflow-hidden">
      <div className="flex-none flex items-center justify-end gap-3 px-4 py-3 border-b bg-card">
        <DashboardHeaderControls />
      </div>
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        {/* Main Content */}
        <div className="flex flex-1 min-h-0 overflow-hidden md:flex-row relative">
          {/* Sidebar - Desktop/tablet */}
          <div
            className="hidden md:flex flex-none border-r bg-card flex-col overflow-hidden"
            style={{ width: 384 }}
          >
            {/* Fixed header section */}
            <div className="flex-none border-b p-3 overflow-y-auto overflow-x-hidden max-h-[60vh]">
              {/* Pet Selector (left column only) */}
              <div className="mb-4">
                <PetSelector compact />
              </div>

              {/* Stats */}
              <div className="mb-4">
                <StatsCard
                  totalParks={parks.length}
                  avgRating={avgRating}
                  nearbyCount={filteredParks.length}
                />
              </div>

              {/* Walking Buddy Panel */}
              <div className="mb-4">
                <WalkingBuddyPanel
                  onOpenModal={() => setShowWalkingBuddy(true)}
                />
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('searchParks')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Scrollable Parks List */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <ListView parks={filteredParks} onParkSelect={handleParkSelect} />
            </div>
          </div>

          {/* Main View Area */}
          <div className="relative min-w-0 flex flex-1 flex-col overflow-hidden">
            {/* Mobile Controls */}
            <div className="border-b bg-card p-3 sm:p-4 md:hidden min-w-0 overflow-hidden">
              {/* Pet Selector (mobile only; desktop selection lives in left sidebar) */}
              {!isDesktop && (
                <div className="mb-3 min-w-0">
                  <PetSelector compact />
                </div>
              )}

              {/* Search & Filter */}
              <div className="flex gap-2 min-w-0">
                {/* Search input (mobile only). On desktop, search lives in the left sidebar. */}
                {!isDesktop && (
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground shrink-0 pointer-events-none" />
                    <Input
                      type="text"
                      placeholder={t('searchParks')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-10 min-h-10 pl-10 text-sm min-w-0"
                    />
                  </div>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setShowFilters(true)}
                  className="relative h-10 w-10 min-h-10 min-w-10 flex-shrink-0 touch-manipulation cursor-pointer"
                  aria-label={t('filters')}
                >
                  <SlidersHorizontal className="h-5 w-5" />
                  {activeFilterCount > 0 && (
                    <Badge className="absolute -right-1 -top-1 h-5 min-w-5 rounded-full px-1 text-xs">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </div>

              {/* View Toggle (mobile only) */}
              {!isDesktop && (
                <>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <Button
                      variant={view === 'map' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setView('map')}
                      className="h-10 min-h-10 touch-manipulation cursor-pointer"
                    >
                      <Map className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{t('mapView')}</span>
                    </Button>
                    <Button
                      variant={view === 'list' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setView('list')}
                      className="h-10 min-h-10 touch-manipulation cursor-pointer cursor-pointer"
                    >
                      <List className="mr-2 h-4 w-4 shrink-0" />
                      <span className="truncate">{t('listView')}</span>
                    </Button>
                  </div>

                  <div className="mt-3 text-center text-sm text-muted-foreground break-words min-w-0">
                    {filteredParks.length}{' '}
                    {t(filteredParks.length === 1 ? 'parkFound' : 'parksFound')}
                  </div>
                </>
              )}
            </div>

            {/* Desktop View Toggle & Results */}
            <div className="hidden md:flex items-center justify-between border-b bg-card px-4 py-3">
              <div className="flex items-center gap-2">
                <Button
                  variant={view === 'map' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setView('map')}
                >
                  <Map className="mr-2 h-4 w-4" />
                  {t('mapView')}
                </Button>
                <Button
                  variant={view === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setView('list')}
                >
                  <List className="mr-2 h-4 w-4" />
                  {t('listView')}
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  {filteredParks.length}{' '}
                  {t(filteredParks.length === 1 ? 'parkFound' : 'parksFound')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(true)}
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <Badge className="ml-2 h-5 min-w-5 rounded-full px-1.5">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </div>
            </div>

            {/* View Content */}
            <div className="flex-1 min-h-0 min-w-0 overflow-hidden p-3 sm:p-4">
              {view === 'map' ? (
                <MapView
                  parks={filteredParks}
                  onParkSelect={handleParkSelect}
                />
              ) : (
                <ListView
                  parks={filteredParks}
                  onParkSelect={handleParkSelect}
                />
              )}
            </div>

            {/* Desktop Filters Panel (keeps dashboard sidebar visible) */}
            {isDesktop && showFilters && (
              <>
                <div
                  className="absolute inset-0 z-40 bg-black/30"
                  onClick={() => setShowFilters(false)}
                />
                <div className="absolute inset-y-0 right-0 z-50 w-full max-w-md border-l bg-card shadow-xl">
                  <FilterPanel onClose={() => setShowFilters(false)} />
                </div>
              </>
            )}

            {/* Desktop Park Detail (keeps dashboard sidebar visible) */}
            {isDesktop && selectedPark && (
              <div className="absolute inset-0 z-50 bg-background">
                <ParkDetail
                  park={selectedPark}
                  onClose={() => setSelectedPark(null)}
                />
              </div>
            )}

            {/* Walking Buddy Modal - Desktop (scoped to Pet Parks area) */}
            {isDesktop && showWalkingBuddy && (
              <>
                {/* Overlay - scoped to Pet Parks container (covers sidebar + main view) */}
                <div
                  className="absolute inset-0 z-40 bg-black/30"
                  onClick={() => setShowWalkingBuddy(false)}
                />
                {/* Modal Content - bottom sheet scoped to Pet Parks */}
                <div
                  className="absolute bottom-0 left-0 right-0 z-50 bg-card border-t shadow-xl animate-in slide-in-from-bottom duration-500"
                  style={{
                    maxHeight: '85vh',
                    overflowY: 'auto',
                  }}
                >
                  <div className="p-4 sm:p-6">
                    <WalkingBuddyPanel
                      onOpenModal={() => setShowWalkingBuddy(true)}
                      onCloseModal={() => setShowWalkingBuddy(false)}
                      showCloseButton={true}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile Filter Sheet */}
        {!isDesktop && (
          <Sheet open={showFilters} onOpenChange={setShowFilters}>
            <SheetContent side="right" className="w-full p-0 sm:max-w-md">
              <SheetTitle className="sr-only">{t('filters')}</SheetTitle>
              <SheetDescription className="sr-only">
                {t('filterParksDescription')}
              </SheetDescription>
              <FilterPanel onClose={() => setShowFilters(false)} />
            </SheetContent>
          </Sheet>
        )}

        {/* Mobile Park Detail Sheet */}
        {!isDesktop && (
          <Sheet
            open={!!selectedPark}
            onOpenChange={() => setSelectedPark(null)}
          >
            <SheetContent side="bottom" className="h-[92vh] p-0 sm:h-[85vh]">
              <SheetTitle className="sr-only">
                {selectedPark?.name || t('parkDetails')}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {t('parkDetailsDescription')}
              </SheetDescription>
              {selectedPark && (
                <ParkDetail
                  park={selectedPark}
                  onClose={() => setSelectedPark(null)}
                />
              )}
            </SheetContent>
          </Sheet>
        )}

        {/* Walking Buddy Sheet - Mobile */}
        {!isDesktop && (
          <Sheet open={showWalkingBuddy} onOpenChange={setShowWalkingBuddy}>
            <SheetContent side="bottom" className="h-[85vh] p-4 sm:h-[75vh]">
              <SheetTitle className="sr-only">{t('walkingBuddy')}</SheetTitle>
              <SheetDescription className="sr-only">
                {t('findCompatiblePartners')}
              </SheetDescription>
              <WalkingBuddyPanel />
            </SheetContent>
          </Sheet>
        )}

        {/* Walking Buddy Floating Button - Mobile Only */}
        <Button
          size="icon"
          className="fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full shadow-lg md:hidden"
          onClick={() => setShowWalkingBuddy(true)}
        >
          <Users className="h-6 w-6" />
          <span className="sr-only">{t('walkingBuddy')}</span>
        </Button>

        {/* Offline Indicator */}
        {isEnabled('offline_mode') && <OfflineIndicator />}
      </div>
    </div>
  );
};

export const PetParks: React.FC<PetParksProps> = ({
  pets,
  selectedPetId,
  onSelectPetId,
}) => {
  const mappedPets: Pet[] = useMemo(() => {
    return (pets ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      breed: p.breed ?? '',
      image: p.imageUrl,
      size: (p.species === 'dog' ? 'medium' : 'small') as Pet['size'],
      energy: 'medium',
      temperament: 'social',
      specialNeeds: [],
    }));
  }, [pets]);

  return (
    <GoogleMapsScriptProvider apiKey={config.googleMapsApiKey}>
      <AppProvider
        pets={mappedPets}
        initialSelectedPetId={selectedPetId ?? null}
        onSelectedPetChange={(petId) => {
          if (petId) onSelectPetId?.(petId);
        }}
      >
        <PetParksContent />
      </AppProvider>
    </GoogleMapsScriptProvider>
  );
};
